import './FixedMge.scss'

import { useCharacters } from "../../../contexts/Characters"
import { useData } from "../../../contexts/Data"
import { capitalize, formatThousands, mgeRating } from "../../../utilities/helpers"
import { formattedDateTime } from "../../../utilities/datetime"
import { Roles, mgeApplicationIconsByReason } from '../../../utilities/constants'
import Tooltip from "../../core/Tooltip/Tooltip"
import Dropdown from "../../core/Dropdown/Dropdown"
import Table from "../../core/Table/Table"
import FixedMgeForm from "../../domain/FixedMgeForm/FixedMgeForm"
import koalition from '../../../api/koalition'
import { Link } from 'react-router'
import { useEffect, useState } from 'react'
import { useModal } from '../../../contexts/Modal'
import WriteList from '../../modals/WriteList/WriteList'
import ReasonIcon from '../../domain/ReasonIcon/ReasonIcon'
import FixedMgeList from '../../domain/FixedMgeList/FixedMgeList'
import useConfirmation from '../../../hooks/ConfimationHook'

const FixedMge = ({}) => {
    const characters = useCharacters()
    const data = useData()
    const modal = useModal()
    const [selectedList, setSelectedList] = useState(null)
    const { isMarked, markOrExecute } = useConfirmation()
    const characterActions = (row) => [
        { name: 'unapply', label: 'Turn application off', onClick: () => resetCharacterApplication(row.character) },
        { name: 'boost', label: 'Toggle boost', onClick: () => toggleProperty('boost', row.character) },
        { name: 'debuff', label: 'Toggle debuff', onClick: () => toggleProperty('debuff', row.character) },
        { name: 'received', label: 'Toggle partial received', onClick: () => toggleProperty('partialReceived', row.character) },
        { name: 'ineligible', label: 'Toggle ineligible', onClick: () => toggleProperty('ineligible', row.character) },
    ]
    const columns = [
        { name: 'actions', label: '', hidden: !characters.authorized([Roles.developer, Roles.admin]), className: 'flex-center-y', content: (row, rowIndex) => (
            <Tooltip text="Actions" align="left" position={rowIndex ? 'top' : 'bottom'}>
                <Dropdown variant="primary small icon outline" items={characterActions(row)}>
                    <img src="/images/icons/pencil.svg" />
                </Dropdown>
            </Tooltip>
        ) },
        { name: 'reason', hidden: !characters.authorized([Roles.developer, Roles.admin]), className: 'flex-center-y', content: (row, rowIndex) => <ReasonIcon reason={row.reason} partialReceived={row.partialReceived} tooltipPosition={rowIndex ? 'top' : 'bottom'} /> },
        { name: 'name', label: '', content: (row) => <Link to={`/characters/${row._id}`} className="name">{row.name}</Link>, className: { cell: 'font-weight-bold' } },
        { name: 'rating', label: 'Rating', content: (row) => (
            <div className="flex-center-y justify-content-end">
                {row.boost && (
                    <div className="boost mr-1">
                        <img src="/images/icons/fire.svg" />
                    </div>
                )}
                {row.debuff && (
                    <div className="boost mr-1">
                        <img src="/images/icons/decrease.svg" />
                    </div>
                )}
                <span>{formatThousands(row.rating)}</span>
            </div>
        ), className: { cell: 'font-weight-bold text-right', heading: 'text-right' } },
        { name: 'power', label: 'Power', content: (row) => formatThousands(row.power), className: { cell: 'text-right text-nowrap', heading: 'text-right' } },
        { name: 'merits', label: 'Merits', content: (row) => formatThousands(row.merits), className: { cell: 'text-right text-nowrap', heading: 'text-right' } },
        { name: 'targetUnitType', label: 'Target Unit Type', content: (row) => capitalize(row.targetUnitType), className: { cell: 'text-center' } },
        { name: 'mercenaryTech', label: 'Tech', className: { cell: 'text-right', heading: 'text-right' } },
    ].filter((row) => !row.hidden)

    const applicants = characters.$state.server && characters.$state.server.accepted.filter((character) => character.mge?.apply).map((character) => ({
        character,
        ...character.mge,
        _id: character._id,
        name: character.name,
        power: character.power,
        merits: character.merits,
        targetUnitType: character.mge.targetUnitType,
        reason: character.mge.reason,
        mercenaryTech: character.mercenaryTech1[character.mge.targetUnitType] + character.mercenaryTech2[character.mge.targetUnitType],
        rating: mgeRating(character),
        received: character.seasonalRecords.find((record) => record.current).receivedFixedMges,
        partialReceived: character.mge.partialReceived,
        className: `${character.seasonalRecords.find((record) => record.current).receivedFixedMges ? 'received' : ''} ${character.mge.ineligible ? 'ineligible' : ''}`.trim()
    })).sort((application1, application2) => application2.rating - application1.rating).sort((application1, application2) => application1.received - application2.received) || []

    const resetCharacterApplication = (character) => {
        koalition.put(`/characters/${character._id}`, { mge: { ...character.mge, apply: false } }).then((response) => {
            toaster.success('Character successfully unapplied')
        }).catch((error) => {
            toaster.error(error)
            console.error(error)
        })
    }

    const toggleProperty = (property, character) => {
        koalition.put(`/characters/${character._id}`, { mge: { ...character.mge, [property]: !character.mge[property] } }).then((response) => {
            toaster.success(`Character updated successfully`)
        }).catch((error) => {
            toaster.error(error)
            console.error(error)
        })
    }

    const onListEditClick = () => {
        modal.open({ title: 'Edit Fixed MGE List', size: 'xl', component: <WriteList />, data: { applicants: applicants.filter((applicant) => !applicant.ineligible), list: data.$state.list } })
    }

    const onNewListClick = () => {
        modal.open({ title: 'New Fixed MGE List', size: 'xl', component: <WriteList />, data: { applicants: applicants.filter((applicant) => !applicant.ineligible) } })
    }

    const onListPublishClick = () => {
        koalition.put(`/lists/${data.$state.list._id}`, { public: !data.$state.list.public })
    }

    const onListArchiveClick = () => {
        koalition.put(`/lists/${data.$state.list._id}`, { archived: !data.$state.list.archived })
    }

    const resetApplications = () => {
        koalition.put('/characters/resetApplications').then((response) => {
            toaster.success('Applications reset successfully')
        }).catch((error) => {
            toaster.error(error)
            console.error(error)
        })
    }

    useEffect(() => {
        if (data.$state.lists) {
            const sameIdList = selectedList !== null && data.$state.lists[selectedList]
            const currentList = data.$state.lists.find((list) => !list.archived && list.public)
            const firstList = data.$state.lists[0]
            let listToSelect
            if (characters.authorized([Roles.developer, Roles.admin])) {
                listToSelect = sameIdList || currentList || firstList
                if (selectedList === null) setSelectedList(data.$state.lists.findIndex((list) => list._id === listToSelect?._id))
            } else {
                listToSelect = currentList
                setSelectedList(null)
            }
            
            data.save('list', listToSelect)
        }
    }, [data.$state.lists, selectedList])

    useEffect(() => {
        if (characters.$state.current) {
            koalition.get('/lists').then((response) => {
                response.data.sort((list1, list2) => list1.week - list2.week)
                data.save('lists', response.data)
            }).catch((error) => {
                toaster.error(error)
                console.error(error)
            })
        }
    }, [characters.$state.current])
    
    return (
        <div className="container-fluid fixed-mge">
            {data.$state.list && (
                <div className="card semi-dark mb-5">
                    <div className="row justify-content-between mb-3">
                        <h3 className="col-auto flex-center-y">
                            <span className="mr-3">List</span>
                            {!!data.$state.lists?.length && selectedList !== null && (
                                <button className="primary small icon mr-2" disabled={selectedList <= 0} onClick={() => setSelectedList(selectedList - 1)}>
                                    <img src="/images/icons/chevron_left.svg" />
                                </button>
                            )}
                            <span>{formattedDateTime(data.$state.list.week, { date: true })}</span>
                            {!!data.$state.lists?.length && selectedList !== null && (
                                <button className="primary small icon ml-2" disabled={selectedList >= (data.$state.lists?.length - 1)} onClick={() => setSelectedList(selectedList + 1)}>
                                    <img src="/images/icons/chevron_right.svg" />
                                </button>
                            )}
                        </h3>

                        {characters.authorized([Roles.developer, Roles.admin]) && (
                            <div className="col-auto flex-center-y my-2">
                                <Tooltip text={`${data.$state.list.archived ? 'Unarchive' : 'Archive'} List`} position="bottom" align="right">
                                    <button className={`primary ${data.$state.list.archived ? '' : 'outline'} small icon mr-2`} onClick={onListArchiveClick}>
                                        <img src="/images/icons/archive.svg" />
                                    </button>
                                </Tooltip>

                                <Tooltip text={`${data.$state.list.public ? 'Unpublish' : 'Publish'} List`} position="bottom" align="right">
                                    <button className={`primary ${data.$state.list.public ? '' : 'outline'} small icon mr-2`} onClick={onListPublishClick}>
                                        <img src="/images/icons/world.svg" />
                                    </button>
                                </Tooltip>

                                <Tooltip text="Edit List" position="bottom" align="right">
                                    <button className="primary small icon" onClick={onListEditClick}>
                                        <img src="/images/icons/pencil.svg" />
                                    </button>
                                </Tooltip>
                            </div>
                        )}
                    </div>

                    <FixedMgeList {...data.$state.list} />
                </div>
            )}

            <div className="row">
                <div className="col-lg-7 mb-4 ranking">
                    <div className="card semi-dark">
                        <header className="row justify-content-between mb-3">
                            <h3 className="col-auto">Ranking</h3>

                            {characters.authorized([Roles.developer, Roles.admin]) && (
                                <div className="col-auto flex-center-y">
                                    <Tooltip text="Reset all applications" position="bottom">
                                        <button className={`${isMarked('applications') ? 'marked danger' : 'primary'} outline small icon mr-3`} onClick={() => markOrExecute('applications', resetApplications)}>
                                            <img src="/images/icons/arrow_return.svg" />
                                        </button>
                                    </Tooltip>

                                    <Tooltip text="New List" position="bottom">
                                        <button className="primary small icon" onClick={onNewListClick}>
                                            <img src="/images/icons/podium.svg" />
                                        </button>
                                    </Tooltip>
                                </div>
                            )}
                        </header>
                        <div className="ranking">
                            <Table columns={columns} rows={applicants} rowNumbers={true} />
                            <p className="mt-3 warning">The ranking is provisional and is only a reference. Final lists will be explicitly published for every MGE occurrence.</p>
                        </div>
                    </div>
                </div>

                <div className="col-lg-5 mb-4">
                    {characters.$state.current && (
                        <div className="card semi-dark">
                            <FixedMgeForm character={characters.$state.current} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default FixedMge